import { useMediaQuery } from "@gluestack-ui/utils/hooks";
import {
  ChevronDown,
  List,
  Map,
  Search,
  SearchX,
  X,
} from "lucide-react-native";
import { FC, useCallback, useMemo, useState } from "react";

import DemographicDistributionEditor from "@/components/panels/DemographicDistributionEditor";
import {
  Box,
  Center,
  HStack,
  Image,
  Input,
  InputField,
  InputIcon,
  InputSlot,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import type {
  CountryValue,
  DemographicDistribution,
  StateDistribution,
} from "@/types/resources";

import { BASE_SCALE, REGION_COUNTRIES, type ListRegion } from "./constants";
import { WorldMap } from "./maps/WorldMap";
import { SelectionPills } from "./SelectionPills";
import StateEditor from "./StateEditor";
import { computeCountryDist, computeStateDist } from "./weightUtils";

const flagUrl = (iso2: string): string =>
  `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`;

/* ── Props ── */

export interface CountryEditorProps {
  countryDistribution: DemographicDistribution<"country">[];
  disabled?: boolean;
  items: { label: string; value: CountryValue }[];
  onChange: (countries: DemographicDistribution<"country">[]) => void;
  readonly?: boolean;
}

/* ── Component ── */

const CountryEditor: FC<CountryEditorProps> = ({
  countryDistribution,
  disabled = false,
  items,
  onChange,
  readonly = false,
}) => {
  const { i18n } = useLocalization();
  const { geographics } = API.Metadata.useGetGeographics();
  const [isDesktop] = useMediaQuery({ minWidth: 1024 });

  const interactionDisabled = disabled || readonly;

  /* ── Derived data ── */

  const countryValues = useMemo(
    () => countryDistribution.map((d) => d.value),
    [countryDistribution]
  );

  /** Countries from geographics that have a map configuration. */
  const mapCountries = useMemo(
    () => geographics.filter((g) => !!g.map),
    [geographics]
  );

  /* ── UI state ── */

  const [viewMode, setViewMode] = useState<"list" | "map">("map");
  const [search, setSearch] = useState("");
  const [countryWeightOpen, setCountryWeightOpen] = useState(false);
  const [stateWeightOpen, setStateWeightOpen] = useState(false);
  const [activeListRegion, setActiveListRegion] = useState<ListRegion | null>(
    null
  );
  const [activeWorldChipIdx, setActiveWorldChipIdx] = useState<number | null>(
    null
  );
  /** "world" or a country code from dropdown. */
  const [mapCanvas, setMapCanvas] = useState<string>("world");

  const activeCountryCode =
    mapCanvas !== "world" ? (mapCanvas as CountryValue) : null;
  const activeGeo = activeCountryCode
    ? (geographics.find((g) => g.code === activeCountryCode) ?? null)
    : null;

  /* ── Active country's state items (from backend geographics) ── */

  const activeStateItems = useMemo(() => {
    if (!activeGeo) return [];
    return activeGeo.states.map((s) => ({
      label: s.label,
      value: s.value,
    }));
  }, [activeGeo]);

  const activeCountryStates = useMemo(() => {
    if (!activeCountryCode) return [];
    const entry = countryDistribution.find(
      (d) => d.value === activeCountryCode
    );
    return entry?.states ?? [];
  }, [activeCountryCode, countryDistribution]);

  const activeCountryStateValues = useMemo(
    () => activeCountryStates.map((s) => s.value),
    [activeCountryStates]
  );

  /** Regions for current country (from backend). */
  const regionChipRows = useMemo(() => {
    if (!activeGeo) return [];
    if (activeGeo.regions.length <= 1) return [];
    return activeGeo.regions.map((r) => ({
      id: r.value,
      label: r.label,
      values: r.states,
    }));
  }, [activeGeo]);

  /* ── World map presets ── */

  const worldChips = useMemo(
    () =>
      [
        {
          center: [20, 5] as [number, number],
          label: i18n.t("audienceBuilder.presetAfrica"),
          listRegion: "africa" as const,
          scale: BASE_SCALE * 2.2,
        },
        {
          center: [105, 35] as [number, number],
          label: i18n.t("audienceBuilder.presetAsia"),
          listRegion: "asia" as const,
          scale: BASE_SCALE * 2.1,
        },
        {
          center: [15, 50] as [number, number],
          label: i18n.t("audienceBuilder.presetEurope"),
          listRegion: "europe" as const,
          scale: BASE_SCALE * 3.5,
        },
        {
          center: [45, 25] as [number, number],
          label: i18n.t("audienceBuilder.presetMiddleEast"),
          listRegion: "middleEast" as const,
          scale: BASE_SCALE * 3.0,
        },
        {
          center: [-98, 38] as [number, number],
          label: i18n.t("audienceBuilder.presetNorthAmerica"),
          listRegion: "northAmerica" as const,
          scale: BASE_SCALE * 2.0,
        },
        {
          center: [125, -15] as [number, number],
          label: i18n.t("audienceBuilder.presetOceania"),
          listRegion: "oceania" as const,
          scale: BASE_SCALE * 2.0,
        },
        {
          center: [-60, -15] as [number, number],
          label: i18n.t("audienceBuilder.presetSouthAmerica"),
          listRegion: "southAmerica" as const,
          scale: BASE_SCALE * 2.0,
        },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [i18n]
  );

  const [focusRegion, setFocusRegion] = useState<{
    center: [number, number];
    scale: number;
  } | null>(null);

  const goToWorldChip = useCallback(
    (idx: number) => {
      if (activeWorldChipIdx === idx) {
        setActiveWorldChipIdx(null);
        setActiveListRegion(null);
        setFocusRegion(null);
        return;
      }
      const chip = worldChips[idx];
      setFocusRegion({ center: chip.center, scale: chip.scale });
      setActiveWorldChipIdx(idx);
      setActiveListRegion(chip.listRegion);
    },
    [activeWorldChipIdx, worldChips]
  );

  /* ── Canvas change ── */

  const handleMapCanvasChange = useCallback((next: string) => {
    setMapCanvas(next);
    setActiveWorldChipIdx(null);
    setActiveListRegion(null);
    setFocusRegion(null);
    setSearch("");
  }, []);

  /* ── Country toggling ── */

  const handleToggleCountry = useCallback(
    (code: CountryValue) => {
      if (interactionDisabled) return;
      const nextCodes = countryValues.includes(code)
        ? countryValues.filter((c) => c !== code)
        : [...countryValues, code];
      if (!nextCodes.includes(code) && mapCanvas === code) {
        setMapCanvas("world");
        setFocusRegion(null);
      }
      const nextDist = computeCountryDist(
        nextCodes,
        countryDistribution,
        items
      );
      const result = nextDist.map((d) => {
        const prev = countryDistribution.find((p) => p.value === d.value);
        return prev?.states?.length ? { ...d, states: prev.states } : d;
      });
      onChange(result);
    },
    [
      countryDistribution,
      countryValues,
      interactionDisabled,
      items,
      mapCanvas,
      onChange,
    ]
  );

  /* ── State toggling (always scoped to active country) ── */

  const updateActiveCountryStates = useCallback(
    (nextStateCodes: string[]) => {
      if (!activeCountryCode) return;
      const prevStates: StateDistribution[] =
        countryDistribution.find((d) => d.value === activeCountryCode)
          ?.states ?? [];
      const newStates = computeStateDist(
        nextStateCodes,
        prevStates,
        activeStateItems
      );
      let dist = countryDistribution;
      if (!dist.some((d) => d.value === activeCountryCode)) {
        dist = computeCountryDist(
          [...countryValues, activeCountryCode],
          countryDistribution,
          items
        );
      }
      const result = dist.map((d) => {
        if (d.value === activeCountryCode) {
          return { ...d, states: newStates.length > 0 ? newStates : undefined };
        }
        const prev = countryDistribution.find((p) => p.value === d.value);
        return prev?.states?.length ? { ...d, states: prev.states } : d;
      });
      onChange(result);
    },
    [
      activeCountryCode,
      activeStateItems,
      countryDistribution,
      countryValues,
      items,
      onChange,
    ]
  );

  const handleToggleState = useCallback(
    (code: string) => {
      if (interactionDisabled) return;
      const nextCodes = activeCountryStateValues.includes(code)
        ? activeCountryStateValues.filter((s) => s !== code)
        : [...activeCountryStateValues, code];
      updateActiveCountryStates(nextCodes);
    },
    [activeCountryStateValues, interactionDisabled, updateActiveCountryStates]
  );

  const handleRegionToggle = useCallback(
    (states: string[]) => {
      if (interactionDisabled || states.length === 0) return;
      const allSelected = states.every((s) =>
        activeCountryStateValues.includes(s)
      );
      const nextCodes = allSelected
        ? activeCountryStateValues.filter((s) => !states.includes(s))
        : [...new Set([...activeCountryStateValues, ...states])];
      updateActiveCountryStates(nextCodes);
    },
    [activeCountryStateValues, interactionDisabled, updateActiveCountryStates]
  );

  /* ── Weight editors ── */

  const handleStateWeightsChange = useCallback(
    (dist: DemographicDistribution[]) => {
      if (!activeCountryCode) return;
      const result = countryDistribution.map((d) =>
        d.value === activeCountryCode
          ? { ...d, states: dist as unknown as StateDistribution[] }
          : d
      );
      onChange(result);
    },
    [activeCountryCode, countryDistribution, onChange]
  );

  const handleCountryWeightsChange = useCallback(
    (dist: DemographicDistribution[]) => {
      const updated = dist as DemographicDistribution<"country">[];
      const result = updated.map((d) => {
        const prev = countryDistribution.find((p) => p.value === d.value);
        return prev?.states ? { ...d, states: prev.states } : d;
      });
      onChange(result);
    },
    [countryDistribution, onChange]
  );

  /* ── Computed display data ── */

  const itemCodes = useMemo(() => new Set(items.map((i) => i.value)), [items]);

  const filteredCountryItems = useMemo(() => {
    let result = items;
    if (activeListRegion) {
      const regionSet = new Set(REGION_COUNTRIES[activeListRegion]);
      result = result.filter((item) => regionSet.has(item.value));
    }
    const q = search.trim().toLowerCase();
    if (q)
      result = result.filter((item) => item.label.toLowerCase().includes(q));
    return result;
  }, [activeListRegion, items, search]);

  const countryOtherPct = Math.max(
    0,
    100 -
      countryDistribution.reduce(
        (sum, d) => sum + Math.round(d.weight * 100),
        0
      )
  );
  const stateOtherPct = Math.max(
    0,
    100 -
      activeCountryStates.reduce(
        (sum, d) => sum + Math.round(d.weight * 100),
        0
      )
  );

  /* ── View toggle ── */

  const ViewToggle = (
    <Pressable
      className="p-0.5 rounded-lg bg-outline-100 flex-row gap-0.5"
      onPress={() => setViewMode((prev) => (prev === "map" ? "list" : "map"))}
    >
      {(
        [
          { Icon: Map, mode: "map" as const },
          { Icon: List, mode: "list" as const },
        ] as const
      ).map(({ Icon, mode }) => (
        <Box
          className={`h-6 w-6 items-center justify-center rounded-md ${viewMode === mode ? "bg-white" : ""}`}
          key={mode}
        >
          <Icon
            color={
              viewMode === mode
                ? "rgb(var(--color-typography-900))"
                : "rgb(var(--color-typography-400))"
            }
            size={13}
          />
        </Box>
      ))}
    </Pressable>
  );

  /* ── Dropdown (replaces MapViewSelect) ── */

  const Dropdown = (
    <Box className="relative flex-shrink-0">
      <select
        aria-label={i18n.t("audienceBuilder.mapViewWorld")}
        className="appearance-none h-7 pl-2.5 pr-8 rounded-md border border-outline-200 bg-white text-xs font-medium text-typography-700 cursor-pointer max-w-[200px]"
        onChange={(e) => handleMapCanvasChange(e.target.value)}
        style={{ color: "rgb(var(--color-typography-700))" }}
        value={mapCanvas}
      >
        <option value="world">{i18n.t("audienceBuilder.mapViewWorld")}</option>
        {mapCountries.map((mc) => (
          <option key={mc.code} value={mc.code}>
            {mc.label}
          </option>
        ))}
      </select>
      <Box className="pointer-events-none absolute right-1.5 top-0 bottom-0 justify-center">
        <ChevronDown color="rgb(var(--color-typography-500))" size={14} />
      </Box>
    </Box>
  );

  /* ── Search placeholder ── */

  const searchPlaceholder =
    mapCanvas === "world"
      ? i18n.t("audienceBuilder.searchCountries")
      : i18n.t("audienceBuilder.searchStates");

  /* ── State distribution editor (inline in toolbar) ── */

  const DistributionEditor = !readonly
    ? mapCanvas !== "world"
      ? activeCountryStates.length > 0 && (
          <DemographicDistributionEditor
            disabled={disabled}
            distribution={activeCountryStates}
            onChange={handleStateWeightsChange}
            title={activeGeo?.label ?? ""}
            totalOptions={activeStateItems.length}
          />
        )
      : countryDistribution.length > 0 && (
          <DemographicDistributionEditor
            distribution={countryDistribution}
            onChange={handleCountryWeightsChange}
            title={i18n.t("audienceBuilder.countries")}
            totalOptions={items.length}
          />
        )
    : null;

  /* ── Region presets (world canvas) / region chip bar (country canvas) ── */

  const worldChipsList = worldChips.map((chip, idx) => (
    <Pressable key={chip.label} onPress={() => goToWorldChip(idx)}>
      <Box
        className={`px-2.5 py-1 rounded-md flex-shrink-0 ${activeWorldChipIdx === idx ? "bg-typography-900" : "bg-white border border-outline-200"}`}
      >
        <Text
          className={`text-xs font-medium ${activeWorldChipIdx === idx ? "text-white" : "text-typography-500"}`}
        >
          {chip.label}
        </Text>
      </Box>
    </Pressable>
  ));

  const regionChipsList = regionChipRows.map((region) => {
    const allSelected = region.values.every((s) =>
      activeCountryStateValues.includes(s)
    );
    return (
      <Pressable
        disabled={interactionDisabled}
        key={region.id}
        onPress={() => handleRegionToggle(region.values)}
      >
        <Box
          className={`px-2.5 py-1 rounded-md flex-shrink-0 ${allSelected ? "bg-primary-500" : "bg-white border border-outline-200"}`}
        >
          <Text
            className={`text-xs font-medium ${allSelected ? "text-white" : "text-typography-500"}`}
          >
            {region.label}
          </Text>
        </Box>
      </Pressable>
    );
  });

  const hasPresets = mapCanvas === "world" || regionChipRows.length > 0;

  /* World presets and per-country region chips share the EXACT same container
     and chip layout — only the labels (and active colour) differ — so switching
     canvases doesn't shift the toolbar.
     `grow` lets the presets fill the remaining toolbar width and scroll on
     desktop (single row). On mobile the presets sit on their own line, so
     `flex:1` must NOT be used — inside a column it collapses the height to 0
     and crops the chips. There we size to content + a little vertical padding. */
  const renderPresets = (grow: boolean) => {
    const chips = mapCanvas === "world" ? worldChipsList : regionChipsList;
    if (chips.length === 0) return null;
    return (
      <div
        style={{
          alignItems: "center",
          display: "flex",
          gap: 6,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          ...(grow
            ? { flex: 1, minWidth: 0 }
            : { paddingBottom: 3, paddingTop: 3 }),
        }}
      >
        {chips}
      </div>
    );
  };

  const SearchInput = viewMode === "list" && (
    <Input
      className={`${isDesktop ? "w-52 h-7" : "w-full h-9"} border border-outline-200 rounded-full bg-background-50`}
      size="sm"
    >
      <InputSlot className="pl-3">
        <InputIcon as={Search} className="text-typography-500" />
      </InputSlot>
      <InputField
        onChangeText={setSearch}
        placeholder={searchPlaceholder}
        value={search}
      />
      {search.length > 0 && (
        <InputSlot className="pr-3" onPress={() => setSearch("")}>
          <InputIcon as={X} className="text-typography-500" />
        </InputSlot>
      )}
    </Input>
  );

  /* ── Render ── */

  return (
    <VStack className="gap-3">
      <Text className="text-sm font-semibold text-typography-900">
        {readonly
          ? i18n.t("audienceBuilder.countries")
          : i18n.t("audienceBuilder.selectCountries")}
      </Text>
      <Box className="border border-outline-200 rounded-2xl overflow-hidden">
        {/* Toolbar */}
        {isDesktop ? (
          <HStack className="items-center justify-between px-4 py-2.5 border-b border-outline-200 bg-background-50">
            <HStack
              className="items-center gap-2 flex-1"
              style={{ minWidth: 0, overflow: "hidden" }}
            >
              {ViewToggle}
              {Dropdown}
              {hasPresets && (
                <>
                  <Box className="w-px h-4 bg-outline-200 mx-0.5 flex-shrink-0" />
                  {renderPresets(true)}
                </>
              )}
            </HStack>
            <HStack
              className="items-center gap-1 flex-shrink-0"
              style={{ flexShrink: 0, marginLeft: 12 }}
            >
              {SearchInput}
              {DistributionEditor}
            </HStack>
          </HStack>
        ) : (
          <VStack className="border-b border-outline-200 bg-background-50">
            {/* Line 1: toggle, dropdown, weights */}
            <HStack className="items-center justify-between gap-2 px-4 py-2.5">
              <HStack className="items-center gap-2">
                {ViewToggle}
                {Dropdown}
              </HStack>
              {DistributionEditor}
            </HStack>
            {/* Line 2: search + presets */}
            {(SearchInput || hasPresets) && (
              <VStack className="gap-2 px-4 pb-2.5">
                {SearchInput}
                {renderPresets(false)}
              </VStack>
            )}
          </VStack>
        )}

        {/* Map / List content */}
        {mapCanvas === "world" ? (
          viewMode === "map" ? (
            <WorldMap
              countryValues={countryValues}
              disabled={interactionDisabled}
              focusRegion={focusRegion}
              itemCodes={itemCodes}
              onToggleCountry={handleToggleCountry}
            />
          ) : (
            <Box
              className="bg-background-0"
              // @ts-expect-error web-only CSS overflowY
              style={{ height: 460, overflowY: "auto" }}
            >
              {filteredCountryItems.length === 0 ? (
                <Center className="py-12">
                  <VStack className="items-center gap-3">
                    <SearchX className="text-typography-400" size={48} />
                    <Text className="text-lg font-medium text-typography-700">
                      {i18n.t("misc.noSearchResults")}
                    </Text>
                    <Text className="text-sm text-typography-500">
                      {i18n.t("misc.noSearchResultsDescription")}
                    </Text>
                  </VStack>
                </Center>
              ) : (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8,
                    padding: 16,
                  }}
                >
                  {filteredCountryItems.map((item) => {
                    const isSelected = countryValues.includes(item.value);
                    return (
                      <Pressable
                        key={item.value}
                        onPress={() => handleToggleCountry(item.value)}
                        // @ts-expect-error web-only CSS
                        style={{
                          cursor: interactionDisabled ? "default" : "pointer",
                          width: isDesktop
                            ? "calc(25% - 6px)"
                            : "calc(50% - 4px)",
                        }}
                      >
                        <Box
                          className={`rounded-lg border ${isSelected ? "bg-primary-50 border-primary-200" : "bg-transparent border-outline-100"}`}
                          style={{ padding: 10 }}
                        >
                          <Text
                            className={`text-xs ${isSelected ? "text-primary-700 font-semibold" : "text-typography-700"}`}
                            numberOfLines={1}
                          >
                            {item.label}
                          </Text>
                        </Box>
                      </Pressable>
                    );
                  })}
                  <Box style={{ height: 8, width: "100%" }} />
                </Box>
              )}
            </Box>
          )
        ) : (
          <StateEditor
            disabled={interactionDisabled}
            mapConfig={activeGeo?.map ?? null}
            onToggleState={handleToggleState}
            search={search}
            states={activeStateItems}
            stateValues={activeCountryStateValues}
            viewMode={viewMode}
          />
        )}

        {/* Selection pills */}
        {mapCanvas !== "world" ? (
          <SelectionPills
            disabled={disabled}
            hasDistribution={activeCountryStates.length > 0}
            items={activeCountryStateValues.map((code) => {
              const item = activeStateItems.find((i) => i.value === code);
              const dist = activeCountryStates.find((d) => d.value === code);
              return {
                code,
                label: item?.label ?? code,
                pct: dist ? Math.round(dist.weight * 100) : 0,
              };
            })}
            onOpenWeights={() => setStateWeightOpen(true)}
            onRemove={!readonly ? (code) => handleToggleState(code) : undefined}
            otherPct={stateOtherPct}
            readonly={readonly}
          />
        ) : (
          <SelectionPills
            disabled={disabled}
            hasDistribution={countryDistribution.length > 0}
            items={countryValues.map((code) => {
              const item = items.find((i) => i.value === code);
              const dist = countryDistribution.find((d) => d.value === code);
              return {
                code,
                label: item?.label ?? code,
                pct: dist ? Math.round(dist.weight * 100) : 0,
                prefix: (
                  <Image
                    alt={code}
                    className="h-[14px] w-[20px] rounded-[3px] border border-outline-100"
                    size="none"
                    source={{ uri: flagUrl(code) }}
                  />
                ),
              };
            })}
            onOpenWeights={() => setCountryWeightOpen(true)}
            onRemove={
              !readonly
                ? (code) => handleToggleCountry(code as CountryValue)
                : undefined
            }
            otherPct={countryOtherPct}
            readonly={readonly}
          />
        )}
      </Box>

      {/* Weight modals */}
      <DemographicDistributionEditor
        disabled={interactionDisabled}
        distribution={countryDistribution}
        onChange={handleCountryWeightsChange}
        onOpenChange={setCountryWeightOpen}
        open={countryWeightOpen}
        title={i18n.t("audienceBuilder.countries")}
        totalOptions={items.length}
      />
      <DemographicDistributionEditor
        disabled={interactionDisabled}
        distribution={activeCountryStates}
        onChange={handleStateWeightsChange}
        onOpenChange={setStateWeightOpen}
        open={stateWeightOpen}
        title={activeGeo?.label ?? ""}
        totalOptions={activeStateItems.length}
      />
    </VStack>
  );
};

export default CountryEditor;
